/**
 * Pi-guided aircraft — the pure half of `PicamControl.tsx`.
 *
 * The aircraft's Pi (`guidance.onboard`, in the separate ardupilot project) tracks a box
 * and, while the pilot holds GUIDED, steers towards it. This file turns a drag into the
 * box the backend relays (`backend/picam_routes.py`, which converts it to the Pi's frame
 * pixels) and the Pi's state into something an operator reads at a glance.
 * docs/PICAM_GUIDANCE.md.
 */
import type { NormPoint } from './roiGeometry';

/** The Pi page refuses a drag of 4 px or less on either axis; so does the backend. */
export const MIN_BOX_PX = 5;
/** The Pi's own page posts /alive at 4 Hz against a 1 s ground timeout. */
export const HEARTBEAT_MS = 250;
export const STATUS_POLL_MS = 300;

export interface Velocity { vx: number | null; vy: number | null; vz: number | null; yaw: number | null; }

export interface Guidance {
  state: string | null;
  reason: string | null;
  track: string | null;
  conf: number | null;
  lock_age: number | null;
  mode: string | null;
  armed: boolean | null;
  pretend: boolean;
  tel_age: number | null;
  sent: number | null;
  note: string | null;
}

export interface PicamTelemetry {
  connected: boolean;
  alt: number | null;
  mode: string | null;
  armed: boolean | null;
  battery: number | null;
  satellites: number | null;
  fix_type: number | null;
}

export interface PicamStatus {
  enabled: boolean;
  reachable?: boolean;
  reason?: string | null;
  drone_id?: string;
  frame?: { w: number; h: number; measured: boolean };
  headline?: string | null;
  cut?: boolean | null;
  guidance?: Guidance;
  decided?: Velocity | null;
  allowed?: Velocity | null;
  backend?: string | null;
  heartbeat_age_s?: number | null;
  telemetry?: PicamTelemetry | null;
  telemetry_error?: string | null;
  /** Who armed it last — their tab's heartbeat is the only one that keeps it engaged. */
  controller?: string | null;
  you_control?: boolean;
  /** The approach speed limit (m/s) and the Pi's fixed list. Absent on a Pi older than
   *  drone-repo bb64a8b — no selector is shown then. Changeable only while CUT. */
  vmax?: number | null;
  vmax_choices?: number[] | null;
  mode_required?: string | null;
}

/** Can the operator change vmax right now? Only from the fixed list, only while CUT —
 *  the Pi refuses (409) otherwise, and it must never change mid-approach. */
export function vmaxEditable(s: PicamStatus | null): boolean {
  return !!s?.enabled && !!s.reachable && s.cut === true
    && Array.isArray(s.vmax_choices) && s.vmax_choices.length > 0;
}

/** Two normalised corners → `{u1,v1,u2,v2}` ordered, or null when too small to lock
 *  at this frame size. */
export function boxFromDrag(
  a: NormPoint, b: NormPoint, frameW: number, frameH: number,
): { u1: number; v1: number; u2: number; v2: number } | null {
  const u1 = Math.min(a[0], b[0]);
  const u2 = Math.max(a[0], b[0]);
  const v1 = Math.min(a[1], b[1]);
  const v2 = Math.max(a[1], b[1]);
  if ((u2 - u1) * frameW < MIN_BOX_PX || (v2 - v1) * frameH < MIN_BOX_PX) return null;
  return { u1, v1, u2, v2 };
}

export type Tone = 'danger' | 'warning' | 'accent' | 'info' | 'muted';

/** What the operator most needs to know, in order: is it cut, is it unreachable, what
 *  is the controller doing. A CUT aircraft is never shown as tracking. */
export function headlineState(s: PicamStatus | null): { label: string; tone: Tone } {
  if (!s) return { label: 'CONNECTING…', tone: 'muted' };
  if (!s.enabled) return { label: 'DISABLED', tone: 'muted' };
  if (!s.reachable) return { label: 'PI NOT ANSWERING', tone: 'danger' };
  if (s.cut) return { label: 'CUT — SENDING NOTHING', tone: 'danger' };
  const state = s.guidance?.state;
  if (!state) return { label: 'STARTING', tone: 'muted' };
  const tone: Tone =
    state === 'tracking' || state === 'closing' ? 'accent'
      : state === 'hold' ? 'info'
        : state === 'lost' || state === 'searching' || state === 'coasting' ? 'warning'
          : 'muted';
  return { label: state.toUpperCase(), tone };
}

/** The Pi can only see the aircraft through its own link; MAVLink to us is the better
 *  witness when we have it, the Pi's text the fallback. */
export function aircraftState(s: PicamStatus | null): { mode: string | null; armed: boolean | null; alt: number | null } {
  const t = s?.telemetry;
  if (t?.connected) return { mode: t.mode, armed: t.armed, alt: t.alt };
  return { mode: s?.guidance?.mode ?? null, armed: s?.guidance?.armed ?? null, alt: null };
}

export function fmtSigned(x: number | null | undefined, digits = 2): string {
  if (x === null || x === undefined || Number.isNaN(x)) return '—';
  return `${x >= 0 ? '+' : ''}${x.toFixed(digits)}`;
}

/** The commanded setpoint in the words the Pi uses. `allowed` is what actually goes to
 *  the flight controller; `decided` is what it would send if not cut / not log-only. */
export function describeCommand(v: Velocity | null | undefined): string {
  if (!v) return 'nothing';
  return `fwd ${fmtSigned(v.vx)}  right ${fmtSigned(v.vy)}  down ${fmtSigned(v.vz)} m/s  yaw ${fmtSigned(v.yaw)} rad/s`;
}

/** Can a drag on the video lock a box right now? Only while CUT — there is no TARGET
 *  mode to enter. Re-targeting mid-approach is CUT, drag, RESUME: a new box never
 *  swaps under an aircraft that is already flying at the old one. */
export function canDrawBox(s: PicamStatus | null): boolean {
  return !!s?.enabled && !!s.reachable && s.cut === true;
}

/** The mode the Pi needs, but only while the aircraft is known to be in another one.
 *  Unknown mode says nothing: the hint is for a wrong mode, not a missing reading. */
export function modeMismatch(s: PicamStatus | null): string | null {
  const need = s?.mode_required;
  if (!need) return null;
  const mode = aircraftState(s).mode;
  return mode && mode !== need ? need : null;
}

const RE_UNPROVEN = /lock age (\d+)(?: frames?)? is under (\d+)/;

/** The Pi's reason line in operator words. A freshly drawn box is held for
 *  `min_lock_age_frames` before the Pi trusts it, and "lock age 14 is under 15 — not
 *  proven yet" read as a fault when it is the expected first second of every lock. */
export function friendlyReason(reason: string | null | undefined): string | null {
  if (!reason) return null;
  const m = RE_UNPROVEN.exec(reason);
  return m ? `locking… (${m[1]}/${m[2]} frames)` : reason;
}
